<template>
  <div class="custom-ui-select-wrapper">
    <div class="custom-ui-select-label-row" v-if="label || info">
      <label :for="inputId" class="custom-ui-select-label" v-if="label" v-text="label" />
      <ui-field-info :text="info" />
    </div>
    <div class="custom-ui-select" :class="[style, classes]">
      <select :id="inputId" v-model="model" :disabled="disabled" name="ui-select">
        <option
          :value="getDefault()"
          :disabled="required"
          selected
          v-if="!required"
          v-text="t(placeholder)"
        />
        <option
          v-for="(item, index) in items"
          :key="index"
          :value="getValue(item)"
          v-text="item[name]"
        />
      </select>
      <span class="custom-ui-select-icon" v-if="hasValidation">
        <i class="success-icon fa-regular fa-circle-check" v-if="!anyError" />
        <i class="danger-icon fa-regular fa-circle-exclamation" v-else />
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { IUISelect } from '@/types/Components/UIKit'
  import { UseI18n } from '@/utils-ts'
  import { computed, useId } from 'vue'

  const { t } = UseI18n()
  const {
    items = [],
    name,
    value,
    size = 'sm',
    disabled = false,
    expanded = false,
    label = '',
    anyError = false,
    hasValidation = false,
    required = false,
    placeholder = 'admin_global_select',
    info = '',
  } = defineProps<IUISelect>()

  const model = defineModel<string | number | object | null | boolean>()

  const inputId = useId()

  const style = computed(() => {
    if (hasValidation) {
      return anyError ? 'danger' : 'success'
    } else {
      return ''
    }
  })

  const classes = computed(() => {
    return [disabled ? 'disabled' : '', `size-${size}`, expanded ? 'expanded' : '']
  })

  const getDefault = () => {
    if (model.value === '') {
      return ''
    } else if (model.value === null) {
      return null
    } else {
      return null
    }
  }

  const getValue = (item: any) => {
    if (value) {
      return item[value]
    } else {
      return item
    }
  }
</script>

<style lang="scss" scoped>
  .custom-ui-select-wrapper {
    @include flexible($align: flex-start, $justify: flex-start, $direction: column);
    position: relative;
    width: 100%;
    gap: 0.5rem;
    .custom-ui-select-label-row {
      @include flexible($align: center, $wrap: wrap);
      gap: 0.35rem;
      .custom-ui-select-label {
        @include column-label($margin: 0);
        width: auto;
      }
    }
    .custom-ui-select {
      @include flexible($align: center, $justify: space-between);
      border: 1px solid $border-color;
      border-radius: 5px;
      padding: calc(0.5em - 2px) 1em;
      cursor: pointer;
      overflow: hidden;
      width: 100%;

      select {
        font-family: $font-family;
        font-weight: 500;
        width: 100%;
        border: none;
        outline: none;
        background: transparent;
        option {
          font-family: $font-family;
          font-weight: 500;
        }
      }
      &.danger {
        border: 1px solid $danger;
      }
      &.success {
        border: 1px solid $success;
      }
      &.disabled {
        cursor: not-allowed;
        border-color: $disabled;
      }
      .custom-ui-select-icon {
        margin-left: 0.5rem;
        @include flexible($align: center, $justify: center);
        .danger-icon {
          color: $danger;
        }
        .success-icon {
          color: $success;
        }
      }
    }
  }
</style>
